import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  FileText,
  Mail,
  Calendar,
  Lightbulb,
  X,
  Send,
  Paperclip,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useVoiceRecognition } from "@core/hooks/useVoiceRecognition";
import { getBrandName } from "@core/services/supportProduct";
import { toTicketSeverity, toTicketType } from "./ticketFields";
import {
  BUG_HINTS,
  FEATURE_HINTS,
  HUMAN_OR_ACCOUNT_HINTS,
  replyMentionsTicket,
} from "./supportEscalation";
import {
  supportChatLog,
  type SupportChatOutcome,
} from "@core/services/SupportChatLogService";
import type {
  VoiceRecognitionState,
  VoiceRecognitionActions,
} from "@core/hooks/useVoiceRecognition";
import { VoiceInputButton } from "@core/components/shared/VoiceInputButton";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";

interface SupportWidgetProps {
  position?: "bottom-right" | "bottom-left";
  primaryColor?: string;
  userId?: string;
  userEmail?: string;
}

// The floating button and widget header are styled via the `primaryColor`
// prop rather than the app's theme (see its default above), so their icon/
// text colors stay literal white -- they need to keep contrasting against
// whatever primaryColor an embedder passes, not just the app's own theme.
const FLOATING_BUTTON_SHADOW_CLASS = "shadow-[0_8px_30px_rgb(0,0,0,0.12)]"; // color-exempt
const FLOATING_BUTTON_HOVER_SHADOW_CLASS =
  "hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)]"; // color-exempt
const HEADER_ICON_TEXT_CLASS = "text-white"; // color-exempt
const HEADER_CONTENT_CLASS = "text-white relative z-10 flex items-center gap-3"; // color-exempt
const HEADER_ICON_CIRCLE_CLASS =
  "w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"; // color-exempt
const HEADER_SUBTITLE_CLASS = "text-sm text-blue-100"; // color-exempt
const HEADER_BUTTON_CLASS =
  "text-white hover:bg-white/20 rounded-full p-2 transition-colors relative z-10"; // color-exempt

export function SupportWidget({
  position = "bottom-right",
  primaryColor = "#374151", // Neutral dark gray — distinct from primary CTAs // color-exempt
}: SupportWidgetProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const brandName = getBrandName();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"chat">("chat");

  // Détection mobile/desktop pour le positionnement (réactif au resize)
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Ouverture déclenchée par un événement global (ex. depuis le sidebar mobile)
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener("support-widget:open", onOpen);
    return () => window.removeEventListener("support-widget:open", onOpen);
  }, []);

  // Nouveaux états pour le chat IA avec KB
  // Restauration depuis sessionStorage si l'utilisateur est parti puis revenu
  // (ex. il a cliqué « Voir la roadmap » et est revenu en arrière).
  const SUPPORT_CHAT_STORAGE_KEY = "doodates:support-chat-history";
  const [chatMessages, setChatMessages] = useState<
    Array<{
      id: string;
      type: "user" | "assistant" | "kb_suggestions";
      content: string;
      kbSuggestions?: Array<{
        title: string;
        content: string;
        relevance: number;
      }>;
      timestamp: Date;
      // Réponse enregistrée en base → notable 👍/👎
      logged?: boolean;
      rating?: 1 | -1 | null;
    }>
  >(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = sessionStorage.getItem(SUPPORT_CHAT_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Les dates sérialisées en JSON sont des strings → on rétablit
      return parsed.map((m: { timestamp?: string | Date }) => ({
        ...(m as object),
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      })) as never;
    } catch {
      return [];
    }
  });
  const [isSearchingKB, setIsSearchingKB] = useState(false);
  const [showTicketOption, setShowTicketOption] = useState(false);

  // Pièces jointes sélectionnées par l'utilisateur (images / PDF).
  // Elles seront uploadées via l'edge function `upload-support-attachment`
  // juste après la création du ticket. Limites alignées avec l'edge function :
  // max 3 fichiers, 5MB chacun, mimes whitelistés.
  const MAX_ATTACHMENTS = 3;
  const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
  const ALLOWED_ATTACHMENT_MIMES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/pdf",
  ];
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showRoadmapOption, setShowRoadmapOption] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState("");
  const [conversationCount, setConversationCount] = useState(0);
  // Un ticket vient d'être créé pour le dernier message : on ne repropose
  // plus rien (ni bannière ni lien « Contacter l'équipe ») tant que
  // l'utilisateur n'a pas écrit de nouveau message — sinon un second clic
  // sur « Oui » créait un doublon.
  const [ticketCreatedForTurn, setTicketCreatedForTurn] = useState(false);
  // Verrou synchrone contre le double clic : `isSubmitting` (state) n'est
  // visible qu'au rendu suivant, un second clic rapide passait encore.
  const ticketSubmittingRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reconnaissance vocale pour le support — langue réactive
  const voiceLang = (() => {
    const lng = i18n.language?.slice(0, 2) || "fr";
    if (lng === "en") return "en-US";
    if (lng === "es") return "es-ES";
    return "fr-FR";
  })();

  const voiceRecognition = useVoiceRecognition({
    lang: voiceLang,
    onTranscriptChange: (transcript) => {
      setMessage((prev) => prev + transcript);
    },
    onError: (error) => {
      console.error("Erreur reconnaissance vocale support:", error);
    },
  });

  // Capture contexte automatique
  const captureContext = () => {
    return {
      page: window.location.pathname,
      url: window.location.href,
      pollId:
        new URLSearchParams(window.location.search).get("pollId") ||
        new URLSearchParams(window.location.search).get("id"),
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      timestamp: new Date().toISOString(),
      logs: (window as any).__consoleLogs?.slice(-10) || [],
      errors: (window as any).__jsErrors?.slice(-5) || [],
    };
  };

  // Intercepter logs console
  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;

    if (!(window as any).__consoleLogs) {
      (window as any).__consoleLogs = [];
      (window as any).__jsErrors = [];

      console.log = (...args) => {
        (window as any).__consoleLogs.push({
          type: "log",
          message: args
            .map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
            .join(" "),
          timestamp: Date.now(),
        });
        originalLog.apply(console, args);
      };

      console.error = (...args) => {
        // Avoid logging our own error logs to prevent recursion
        const message = args
          .map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
          .join(" ");

        // Skip if this is a DooDates Error log (from our own error handling)
        if (message.includes("🚨 DooDates Error:")) {
          originalError.apply(console, args);
          return;
        }

        (window as any).__jsErrors.push({
          type: "error",
          message,
          stack: new Error().stack,
          timestamp: Date.now(),
        });
        originalError.apply(console, args);
      };
    }

    return () => {
      // Cleanup if needed, though usually we keep it alive
    };
  }, []);

  // Nouvelle fonction pour gérer les messages utilisateurs avec IA conversationnelle
  const handleUserMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    // Anti-spam : ne pas enchaîner des submits tant qu'une requête IA est
    // en cours (le textarea est déjà disabled via isSearchingKB, mais le
    // bouton submit peut être cliqué plus vite que le render, et Enter
    // contourne aussi cette protection).
    if (isSearchingKB || isSubmitting) return;

    // Ajouter le message utilisateur au chat
    const userText = message.trim();
    const userMessageId = crypto.randomUUID();
    // Enregistre un échange qui s'est soldé par la réponse de repli.
    const logFailedTurn = (assistantMessageId: string, content: string) =>
      void supportChatLog
        .logTurn({
          userMessageId,
          userMessage: userText,
          assistantMessageId,
          assistantMessage: content,
          isError: true,
          locale: i18n.language?.slice(0, 2),
        })
        .then(() => supportChatLog.markOutcome("ticket_proposed"));
    const userMessage = {
      id: userMessageId,
      type: "user" as const,
      content: message.trim(),
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setLastUserMessage(message.trim());
    // Les options (ticket / roadmap) portent sur le tour précédent : on les
    // retire dès qu'un nouveau message part, la réponse à venir décidera
    // seule de ce qu'elle propose. Sans ça, des boutons roadmap restaient
    // affichés sous une réponse sans rapport.
    setShowTicketOption(false);
    setShowRoadmapOption(false);
    setTicketCreatedForTurn(false);
    setMessage(""); // Vider le champ de saisie
    setIsSearchingKB(true); // Activer l'état de recherche
    setConversationCount((prev) => prev + 1);

    try {
      // Importer le service IA
      const { SupportAIService } =
        await import("../../services/SupportAIService");
      const supportAIService = SupportAIService.getInstance();

      console.log("🤖 [WIDGET] Calling generateChatResponse with:", {
        message: message.trim(),
        historyLength: chatMessages.length,
      });

      // Générer une réponse IA conversationnelle courte
      const history = chatMessages.map((m) => ({
        role: m.type === "user" ? ("user" as const) : ("assistant" as const),
        content: m.content,
      }));
      const aiResponse = await supportAIService.generateChatResponse(
        message.trim(),
        history,
      );

      console.log("🤖 [WIDGET] AI Response received:", aiResponse);

      setIsSearchingKB(false);

      if (aiResponse) {
        // Ajouter la réponse IA au chat
        const assistantMessage = {
          id: crypto.randomUUID(),
          type: "assistant" as const,
          content: aiResponse.suggestedResponse,
          timestamp: new Date(),
          logged: true,
        };

        setChatMessages((prev) => [...prev, assistantMessage]);
        // Journal best-effort. Classification et issue ne s'écrivent qu'une
        // fois l'échange enregistré (sinon la ligne n'existe pas encore).
        const turnLogged = supportChatLog.logTurn({
          userMessageId,
          userMessage: userText,
          assistantMessageId: assistantMessage.id,
          assistantMessage: assistantMessage.content,
          isError: false,
          locale: i18n.language?.slice(0, 2),
        });

        // Détection simple d'un message d'au revoir : aucune bannière, pour
        // ne pas polluer la fin de conversation (les options du tour
        // précédent ont déjà été retirées à l'envoi). On ne déclenche que
        // pour les messages COURTS (≤ 80 caractères) pour éviter qu'un
        // "cordialement" en fin d'un vrai bug long ne fasse passer tout le
        // message pour un au revoir.
        const farewellRegex =
          /\b(au revoir|bonne (journée|soirée|continuation)|à (bientôt|plus tard|la prochaine)|à plus|bye|ciao|bonne nuit)\b/i;
        const isFarewell =
          userText.length <= 80 && farewellRegex.test(userText);
        if (isFarewell) return;

        // Filet n°1 : la réponse affichée recommande elle-même un ticket.
        // Le bouton doit alors être là, quel que soit l'avis du classifieur
        // (cas vécu le 29/09 : réponse « je vous invite à créer un ticket »,
        // action "conversation", aucun bouton).
        const replyRecommendsTicket = replyMentionsTicket(
          aiResponse.suggestedResponse,
        );

        // Analyse intelligente du message avec la nouvelle méthode améliorée
        try {
          console.log(
            "AI Analysis - Using new classifyAndSuggestAction method",
          );
          const classification =
            // Classer le message courant : `lastUserMessage` (state) vaut
            // encore le message PRÉCÉDENT à ce stade du rendu.
            await supportAIService.classifyAndSuggestAction(userText);

          console.log("AI Classification Result:", classification);

          if (classification) {
            void turnLogged.then(() =>
              supportChatLog.classify({
                messageId: userMessageId,
                classification: classification.classification,
                action: classification.action,
                confidence: classification.confidence,
              }),
            );
          }
          const classificationType =
            classification?.classification?.toLowerCase();
          const action = classification?.action?.toLowerCase();
          const confidence = classification?.confidence || 0;

          console.log(
            "Classification:",
            classificationType,
            "Action:",
            action,
            "Confidence:",
            confidence,
          );

          // Filets locaux : le classifieur LLM retombe parfois tout en
          // "question/conversation". On ajoute des heuristiques texte pour
          // rattraper les cas les plus nets (idée vs bug vs besoin humain).
          const looksLikeBug = BUG_HINTS.test(userText);
          const needsHuman = HUMAN_OR_ACCOUNT_HINTS.test(userText);
          const looksLikeFeature =
            FEATURE_HINTS.test(userText) && !looksLikeBug && !needsHuman;

          // Seuil de confiance à 0.75 pour ignorer les classifications
          // hésitantes du LLM (< 0.75) et éviter trop de faux positifs.
          const CONFIDENCE_THRESHOLD = 0.75;
          const trustLLM = confidence >= CONFIDENCE_THRESHOLD;
          const effectiveAction = trustLLM ? action : null;

          // On se base d'abord sur l'action proposée par le classifieur
          // (plus fiable que le label : ex. "complex" + "roadmap" doit
          // quand même ouvrir la bannière roadmap).
          let proposed: SupportChatOutcome | null = null;
          if (
            effectiveAction === "ticket" ||
            looksLikeBug ||
            needsHuman ||
            replyRecommendsTicket
          ) {
            proposed = "ticket_proposed";
          } else if (
            effectiveAction === "roadmap" ||
            looksLikeFeature ||
            // Le classifieur a bien identifié une demande de fonctionnalité
            // mais a proposé "conversation" : on pousse quand même vers la
            // roadmap.
            (trustLLM && classificationType === "feature")
          ) {
            proposed = "roadmap_proposed";
          } else if (
            effectiveAction !== "conversation" &&
            conversationCount >= 5
          ) {
            // Après 5+ messages sans classification claire, proposer un ticket.
            proposed = "ticket_proposed";
          }

          console.log("Proposed option:", proposed ?? "none");
          if (proposed === "ticket_proposed") setShowTicketOption(true);
          if (proposed === "roadmap_proposed") setShowRoadmapOption(true);
          if (proposed) {
            const outcome = proposed;
            void turnLogged.then(() => supportChatLog.markOutcome(outcome));
          }
        } catch (analysisError) {
          console.warn(
            "Error during AI analysis, using fallback:",
            analysisError,
          );
          if (replyRecommendsTicket || conversationCount >= 5) {
            setShowTicketOption(true);
          }
        }
      } else {
        console.log("⚠️ [WIDGET] AI Response is null, using fallback");
        // Message d'erreur IA plus utile
        const errorMessage = {
          id: crypto.randomUUID(),
          type: "assistant" as const,
          content: t("errors:support_technical_bug_ticket"),
          timestamp: new Date(),
        };

        setChatMessages((prev) => [...prev, errorMessage]);
        setShowTicketOption(true);
        logFailedTurn(errorMessage.id, errorMessage.content);
      }
    } catch (error) {
      console.error("❌ [WIDGET] Error during AI response generation:", error);
      setIsSearchingKB(false);

      // En cas d'erreur, proposer directement un ticket
      const errorMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: t("errors:support_technical_bug_ticket"),
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, errorMessage]);
      setShowTicketOption(true);
      logFailedTurn(errorMessage.id, errorMessage.content);
    }
  };

  const proceedToTicketCreation = async (userMessage: string) => {
    // Un envoi est déjà en cours (double clic) : on ignore.
    if (ticketSubmittingRef.current) return;

    // Validation: email obligatoire pour utilisateurs non connectés
    if (!user && !userEmail.trim()) {
      alert(t("errors:support_email_required"));
      return;
    }

    // Validation basique de l'email
    if (!user && userEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      alert(t("errors:invalid_email"));
      return;
    }

    ticketSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const context = captureContext();

      // Capturer un screenshot de la page
      let screenshot: string | null = null;
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(document.body, {
          width: window.innerWidth,
          height: window.innerHeight,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          scrollY: -window.scrollY,
          scrollX: -window.scrollX,
        });
        screenshot = canvas.toDataURL("image/png");
        console.log("📸 Screenshot captured successfully");
      } catch (screenshotError) {
        console.warn("Failed to capture screenshot:", screenshotError);
        // Continue sans screenshot si la capture échoue
      }

      // MIGRATION: Remplacer l'appel API (qui ne marche qu'avec Vercel) par un appel direct Supabase
      // Importé dynamiquement pour éviter les problèmes d'import au niveau du fichier
      const { supabase } = await import("../../lib/supabase");
      const ticketId = crypto.randomUUID();

      // Déterminer l'email à utiliser
      const emailToUse = user?.email || userEmail;

      // Analyse IA du ticket (en parallèle de la génération du titre)
      const { SupportAIService } =
        await import("../../services/SupportAIService");
      const supportAIService = SupportAIService.getInstance();

      // Analyser le screenshot si disponible
      let visualDiagnostic: string | null = null;
      if (screenshot) {
        console.log("📸 Analyzing screenshot with Vision AI...");
        visualDiagnostic = await supportAIService.analyzeScreenshot(
          screenshot,
          userMessage,
        );
        console.log("👁️ Visual diagnostic:", visualDiagnostic);
      }

      const [aiAnalysis, generatedTitle] = await Promise.all([
        supportAIService.analyzeTicket(userMessage, context),
        supportAIService.generateTicketTitle(userMessage),
      ]);

      console.log("🎫 AI Analysis:", aiAnalysis);
      console.log("🎫 Generated Title:", generatedTitle);

      // Utiliser les résultats de l'analyse IA si disponibles
      // Valeurs bornées aux CHECK de support_tickets (cf. ticketFields.ts).
      const severity = toTicketSeverity(aiAnalysis?.severity);
      const ticketType = toTicketType(aiAnalysis?.classification);

      // Obtenir les SLA min/max pour le message
      const slaDays = SupportAIService.getSLADays(severity);
      const slaText = SupportAIService.getSLAText(severity);
      const severityToPriority: Record<string, number> = {
        low: 0,
        normal: 1,
        high: 2,
        critical: 3,
        blocker: 3,
      };

      // Insérer le ticket dans Supabase.
      // NB : le schéma réel n'a PAS de colonne `message` ni `user_email` —
      // on utilise `email` et on stocke le message initial dans
      // `metadata.message` (convention lue par TicketSlidePanel côté admin,
      // ligne ~470 : `ticket.metadata?.message || ticket.subject`).
      //
      // PAS de `.select()` après l'insert : l'identifiant est généré ici, rien
      // n'a besoin d'être relu, et un invité n'a plus le droit de relire la
      // table (faille « user_id IS NULL » fermée le 28/09/2026). Un
      // `.select()` repasserait en `return=representation` et ferait échouer
      // tout envoi sans compte.
      const { error } = await supabase.from("support_tickets").insert({
        id: ticketId,
        subject: generatedTitle || t("support_default_subject"),
        type: ticketType,
        severity: severity,
        priority: severityToPriority[severity],
        status: "open",
        email: emailToUse,
        user_id: user?.id,
        metadata: {
          message: userMessage,
          context,
          screenshot: screenshot ? screenshot.substring(0, 1000) + "..." : null,
          visual_diagnostic: visualDiagnostic,
          ai_analysis: aiAnalysis,
        },
      });

      if (error) {
        console.error("❌ Error inserting ticket:", error);
        throw error;
      }

      console.log("✅ Ticket created successfully:", ticketId);
      void supportChatLog.markOutcome("ticket_created", ticketId);

      // Upload des pièces jointes (si présentes) maintenant que le ticket existe.
      // L'edge function utilise le service_role pour contourner RLS et stocke
      // dans `ticket.metadata.attachments` (format lu par TicketSlidePanel).
      let attachmentsUploaded = true;
      if (selectedFiles.length > 0) {
        attachmentsUploaded = await uploadAttachmentsForTicket(
          ticketId,
          selectedFiles,
        );
      }

      // Ajouter un message de confirmation dans le chat
      const attachmentNote =
        selectedFiles.length === 0
          ? ""
          : attachmentsUploaded
            ? ` ${t("support_attachments_sent", { count: selectedFiles.length })}`
            : ` ${t("errors:support_attachments_failed")}`;

      const confirmationMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: `${t("support_ticket_created", { id: ticketId.substring(0, 8), sla: slaText })}${attachmentNote}`,
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, confirmationMessage]);
      // Ticket créé : on retire la proposition (sinon « Oui » restait
      // cliquable sous la confirmation et créait un doublon).
      setShowTicketOption(false);
      setTicketCreatedForTurn(true);
      // Reset de la sélection une fois envoyée
      setSelectedFiles([]);
      setAttachmentError(null);
    } catch (error) {
      console.error("❌ Error creating ticket:", error);

      // Message d'erreur spécifique pour les utilisateurs non connectés
      const isAuthError =
        (error &&
          typeof error === "object" &&
          "message" in error &&
          (error.message as string).toLowerCase().includes("permission")) ||
        (error.message as string).toLowerCase().includes("auth") ||
        (error.message as string).toLowerCase().includes("row level security");

      const errorMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content:
          isAuthError && !user
            ? t("errors:support_ticket_login_required")
            : t("errors:support_ticket_creation_failed"),
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, errorMessage]);
    } finally {
      ticketSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Gestion des pièces jointes
  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setAttachmentError(null);
    const incoming = Array.from(fileList);

    // Refuser les fichiers non supportés / trop gros AVANT de les ajouter
    for (const f of incoming) {
      if (!ALLOWED_ATTACHMENT_MIMES.includes(f.type)) {
        setAttachmentError(
          t("errors:support_attachment_bad_format", { name: f.name }),
        );
        return;
      }
      if (f.size > MAX_ATTACHMENT_SIZE) {
        setAttachmentError(
          t("errors:support_attachment_too_large", {
            name: f.name,
            size: (f.size / 1024 / 1024).toFixed(1),
          }),
        );
        return;
      }
    }

    setSelectedFiles((prev) => {
      const combined = [...prev, ...incoming];
      if (combined.length > MAX_ATTACHMENTS) {
        setAttachmentError(
          t("errors:support_attachment_too_many", { max: MAX_ATTACHMENTS }),
        );
        return combined.slice(0, MAX_ATTACHMENTS);
      }
      return combined;
    });

    // Reset l'input pour permettre de re-sélectionner le même fichier après suppression
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  // Upload des pièces jointes après création du ticket.
  // On tolère un échec ici : le ticket est déjà créé, on informe juste
  // l'utilisateur dans le chat si les pièces jointes n'ont pas pu remonter.
  const uploadAttachmentsForTicket = async (
    ticketId: string,
    files: File[],
  ): Promise<boolean> => {
    if (files.length === 0) return true;
    try {
      const { supabase } = await import("../../lib/supabase");
      const formData = new FormData();
      formData.append("ticketId", ticketId);
      for (const f of files) formData.append("files", f, f.name);

      const { data, error } = await supabase.functions.invoke(
        "upload-support-attachment",
        { body: formData },
      );
      if (error) {
        console.error("❌ Attachment upload failed:", error);
        return false;
      }
      console.log("📎 Attachments uploaded:", data);
      return true;
    } catch (err) {
      console.error("❌ Attachment upload threw:", err);
      return false;
    }
  };

  // Fonctions utilitaires pour le widget
  const resetChat = () => {
    setChatMessages([]);
    setMessage("");
    setShowTicketOption(false);
    setShowRoadmapOption(false);
    setLastUserMessage("");
    setConversationCount(0);
    setTicketCreatedForTurn(false);
    setSelectedFiles([]);
    setAttachmentError(null);
  };

  // 👍/👎 : un second clic sur la même note l'annule.
  const rateMessage = (messageId: string, rating: 1 | -1) => {
    const current = chatMessages.find((m) => m.id === messageId)?.rating;
    const next = current === rating ? null : rating;
    setChatMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, rating: next } : m)),
    );
    void supportChatLog.rate(messageId, next);
  };

  const startNewConversation = () => {
    supportChatLog.resetConversation();
    if (chatMessages.length > 0) {
      // Ajouter un message de confirmation de nouvelle conversation
      const resetMessage = {
        id: crypto.randomUUID(),
        type: "assistant" as const,
        content: t("support_new_conversation"),
        timestamp: new Date(),
      };
      setChatMessages([resetMessage]);
    }
    resetChat();
  };

  const handleTicketOption = (createTicket: boolean) => {
    if (createTicket) {
      // Après restauration depuis sessionStorage, `lastUserMessage` est vide :
      // on reprend alors le dernier message utilisateur affiché.
      const lastFromHistory =
        [...chatMessages].reverse().find((m) => m.type === "user")?.content ??
        "";
      void proceedToTicketCreation(lastUserMessage || lastFromHistory);
    } else {
      setShowTicketOption(false);
    }
  };

  // Escalade toujours accessible : dès qu'un échange a eu lieu, un lien
  // discret « Contacter l'équipe » ouvre la proposition de ticket (même
  // bannière, donc même champ e-mail pour les invités). C'est le filet qui
  // ne dépend d'aucune formulation de l'IA ni du classifieur.
  const hasUserMessage = chatMessages.some((m) => m.type === "user");
  const showContactTeamLink =
    hasUserMessage &&
    !showTicketOption &&
    !ticketCreatedForTurn &&
    !isSearchingKB &&
    !isSubmitting;

  const handleRoadmapOption = async (goToRoadmap: boolean) => {
    if (goToRoadmap) {
      // Attendre l'écriture (bornée) : la navigation pleine page l'annulerait.
      await Promise.race([
        supportChatLog.markOutcome("roadmap_clicked"),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
      // Rediriger vers la roadmap en pré-remplissant le formulaire
      // « Proposer une fonctionnalité » avec le dernier message utilisateur.
      // La page roadmap lit ce param pour ouvrir le modal et remplir le champ.
      const suggestion = encodeURIComponent(lastUserMessage || "");
      window.location.href = suggestion
        ? `/roadmap?suggest=${suggestion}`
        : "/roadmap";
    } else {
      setShowRoadmapOption(false);
    }
  };

  // Effet pour scroller vers le bas du chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Persister la conversation pour survivre à une navigation + retour
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (chatMessages.length === 0) {
        sessionStorage.removeItem(SUPPORT_CHAT_STORAGE_KEY);
      } else {
        sessionStorage.setItem(
          SUPPORT_CHAT_STORAGE_KEY,
          JSON.stringify(chatMessages),
        );
      }
    } catch {
      // quota exceeded ou mode privé — pas bloquant
    }
  }, [chatMessages]);

  // Raccourci clavier Shift+?
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === "?") {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // On /create, the page has its own chat input + send button at the bottom.
  // We don't hide the widget — we lift it above that input area so it doesn't
  // cover the send button. ~96px clears a 2-line textarea + buttons + margin.
  const isOnCreatePage = pathname.startsWith("/create");
  const chatInputClearance = isOnCreatePage && isMobile ? 96 : 0;
  const widgetBottom = `calc(${isMobile ? "1rem" : "1.5rem"} + env(safe-area-inset-bottom) + var(--bottom-nav-height, 0px) + ${chatInputClearance}px)`;

  return (
    <div className="z-[70] font-sans" style={{ zIndex: 9999 }}>
      {/* Bouton flottant — masqué sur mobile (point d'entrée via le sidebar) */}
      {!isOpen && !isMobile && (
        <button
          onClick={() => {
            console.log("🎯 [WIDGET] Click event received - opening widget", {
              isOpen,
              position,
            });
            setIsOpen(true);
          }}
          className={`fixed ${position === "bottom-right" ? "right-6" : "left-6"}
            w-14 h-14 rounded-full ${FLOATING_BUTTON_SHADOW_CLASS} flex items-center justify-center
            transition-all duration-300 hover:scale-110 ${FLOATING_BUTTON_HOVER_SHADOW_CLASS} focus:outline-none focus:ring-4 focus:ring-ring`}
          style={{
            backgroundColor: primaryColor,
            // Sit above iOS Safari home indicator, the bottom navigation tabs
            // (--bottom-nav-height set by MobileNavigationTabs when mounted),
            // AND the on-page chat input on /create.
            bottom: widgetBottom,
          }}
          aria-label={t("support_open")}
        >
          {/* Mobile: 20px (Apple HIG-aligned 44px button); Desktop: 24px (industry-standard 48px button). */}
          <Bot
            className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} ${HEADER_ICON_TEXT_CLASS}`}
          />
        </button>
      )}

      {/* Widget ouvert */}
      {isOpen && (
        <div
          className={`fixed ${
            isMobile
              ? "inset-x-2 bottom-2 w-auto max-h-[85vh]"
              : `${position === "bottom-right" ? "right-6" : "left-6"} w-[400px] h-[600px] max-h-[85dvh] max-w-[calc(100vw-32px)]`
          } bg-card rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ease-out border border-border`}
          style={isMobile ? undefined : { bottom: widgetBottom }}
        >
          {/* Header */}
          <div
            className="p-5 flex items-center justify-between relative overflow-hidden"
            style={{ backgroundColor: primaryColor }}
          >
            {/* Pattern de fond subtil */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "16px 16px",
              }}
            ></div>

            <div className={HEADER_CONTENT_CLASS}>
              <div className={HEADER_ICON_CIRCLE_CLASS}>
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg tracking-tight leading-tight">
                  {t("support_assistant_title", { brand: brandName })}
                </h3>
                <p className={HEADER_SUBTITLE_CLASS}>
                  {t("support_assistant_subtitle")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Bouton Nouvelle conversation - seulement si des messages existent */}
              {chatMessages.length > 1 && (
                <button
                  onClick={startNewConversation}
                  className={HEADER_BUTTON_CLASS}
                  aria-label={t("support_new_conversation_label")}
                  title={t("support_new_conversation_label")}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className={HEADER_BUTTON_CLASS}
                aria-label={t("close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body - Uniquement le chat */}
          <div className="flex-1 overflow-y-auto bg-muted flex flex-col">
            <div className="flex-1 flex flex-col p-4 w-full h-full">
              {/* Messages du chat */}
              <div className="flex-1 space-y-4 mb-4 overflow-y-auto">
                {/* Message de bienvenue de l'IA */}
                {chatMessages.length === 0 && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-card border border-border shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-foreground">
                      <p>
                        {t("support_welcome_greeting", { brand: brandName })}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {t("support_welcome_capabilities")}
                      </p>
                      <p className="mt-3 text-xs text-primary">
                        {t("support_welcome_prompt")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Messages du chat */}
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 max-w-[85%] ${msg.type === "user" ? "ml-auto" : ""}`}
                  >
                    {msg.type === "user" ? (
                      <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0 flex items-center justify-center">
                        <span className="text-xs text-muted-foreground font-semibold">
                          {t("support_you")}
                        </span>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`border shadow-sm rounded-2xl rounded-tl-none p-4 text-sm ${msg.type === "user" ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border text-foreground"}`}
                    >
                      <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-foreground prose-strong:text-foreground prose-a:text-primary">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          rehypePlugins={[rehypeSanitize]}
                          components={{
                            a: ({ href, children, ...props }) => (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 underline transition-colors"
                                {...props}
                              >
                                {children}
                              </a>
                            ),
                            strong: ({ children, ...props }) => (
                              <strong
                                className="text-foreground font-semibold"
                                {...props}
                              >
                                {children}
                              </strong>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>

                      {/* Suggestions KB (ancien système - à supprimer complètement) */}
                      {msg.type === "kb_suggestions" && msg.kbSuggestions && (
                        <div className="mt-3 space-y-2">
                          {msg.kbSuggestions.map((article, index) => (
                            <div
                              key={index}
                              className="p-3 bg-muted rounded-lg border border-border hover:border-primary transition-colors"
                            >
                              <h4 className="font-medium text-primary text-sm mb-2">
                                {article.title}
                              </h4>
                              <div className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-4">
                                {article.content}
                              </div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {t("support_relevance")}{" "}
                                {Math.round(article.relevance * 100)}%
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {msg.type === "assistant" && msg.logged && (
                        <div className="mt-2 flex items-center gap-1 text-muted-foreground">
                          <button
                            type="button"
                            onClick={() => rateMessage(msg.id, 1)}
                            aria-label={t("support_rate_helpful")}
                            title={t("support_rate_helpful")}
                            aria-pressed={msg.rating === 1}
                            className={`p-1 rounded hover:bg-muted transition-colors ${msg.rating === 1 ? "text-primary" : ""}`}
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => rateMessage(msg.id, -1)}
                            aria-label={t("support_rate_not_helpful")}
                            title={t("support_rate_not_helpful")}
                            aria-pressed={msg.rating === -1}
                            className={`p-1 rounded hover:bg-muted transition-colors ${msg.rating === -1 ? "text-destructive" : ""}`}
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Message de recherche KB */}
                {isSearchingKB && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-card border border-border shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p>{t("support_searching")}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Question de création de ticket */}
                {showTicketOption && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-card border border-border shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-foreground">
                      <p className="mb-3">{t("support_ticket_prompt")}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleTicketOption(true)}
                          disabled={isSubmitting}
                          aria-busy={isSubmitting}
                          className="px-3 py-1 bg-success hover:bg-success/90 text-success-foreground text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("support_ticket_yes")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTicketOption(false)}
                          disabled={isSubmitting}
                          className="px-3 py-1 bg-muted hover:bg-accent text-foreground text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("support_ticket_no")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Question de proposition de roadmap */}
                {showRoadmapOption && (
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-success/20 flex-shrink-0 flex items-center justify-center">
                      <Lightbulb className="w-4 h-4 text-success" />
                    </div>
                    <div className="bg-card border border-border shadow-sm rounded-2xl rounded-tl-none p-4 text-sm text-foreground">
                      <p className="mb-3">{t("support_roadmap_prompt")}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRoadmapOption(true)}
                          className="px-3 py-1 bg-primary hover:bg-primary/90 text-primary-foreground text-xs rounded transition-colors"
                        >
                          {t("support_roadmap_yes")}
                        </button>
                        <button
                          onClick={() => handleRoadmapOption(false)}
                          className="px-3 py-1 bg-muted hover:bg-accent text-foreground text-xs rounded transition-colors"
                        >
                          {t("support_roadmap_no")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Escalade toujours disponible après un échange */}
                {showContactTeamLink && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowTicketOption(true)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {t("support_contact_team")}
                    </button>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Formulaire au bas du chat */}
              <form
                onSubmit={handleUserMessage}
                className="bg-muted p-3 rounded-2xl border border-border shadow-sm relative z-50 pointer-events-auto focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent transition-shadow"
              >
                {/* Email uniquement si on va créer un ticket */}
                {!user && showTicketOption && (
                  <div className="px-2 pb-2 border-b border-border mb-2">
                    <input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder={t("support_email_placeholder")}
                      className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      disabled={isSubmitting || isSearchingKB}
                    />
                  </div>
                )}
                {/* Aperçu des pièces jointes sélectionnées */}
                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-1 pb-2 border-b border-border mb-2">
                    {selectedFiles.map((file, index) => {
                      const isImage = file.type.startsWith("image/");
                      const previewUrl = isImage
                        ? URL.createObjectURL(file)
                        : null;
                      return (
                        <div
                          key={`${file.name}-${index}`}
                          className="relative group flex items-center gap-2 bg-muted rounded-lg p-1 pr-2 border border-border"
                          title={`${file.name} — ${(file.size / 1024).toFixed(0)} Ko`}
                        >
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={file.name}
                              className="w-10 h-10 object-cover rounded"
                              onLoad={() => URL.revokeObjectURL(previewUrl)}
                            />
                          ) : (
                            <div className="w-10 h-10 flex items-center justify-center bg-card rounded">
                              <FileText className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                          <span className="text-xs text-foreground max-w-[120px] truncate">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            disabled={isSubmitting}
                            className="ml-1 p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={t("support_remove_file", {
                              name: file.name,
                            })}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {attachmentError && (
                  <div className="px-2 pb-2 text-xs text-destructive">
                    {attachmentError}
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("support_message_placeholder")}
                    className="flex-1 px-3 py-2 max-h-[80px] min-h-[40px] text-sm resize-none focus:outline-none bg-transparent text-foreground placeholder:text-muted-foreground"
                    disabled={
                      isSubmitting ||
                      isSearchingKB ||
                      voiceRecognition.isListening
                    }
                    rows={Math.min(3, Math.max(1, message.split("\n").length))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (message.trim() && !isSubmitting && !isSearchingKB)
                          handleUserMessage(e as any);
                      }
                    }}
                  />

                  {/* Bouton pièce jointe */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => handleFilesSelected(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={
                      isSubmitting ||
                      isSearchingKB ||
                      selectedFiles.length >= MAX_ATTACHMENTS
                    }
                    className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 disabled:hover:bg-transparent transition-colors flex items-center justify-center shrink-0"
                    aria-label={t("support_attach_file")}
                    title={
                      selectedFiles.length >= MAX_ATTACHMENTS
                        ? t("support_attach_max", { max: MAX_ATTACHMENTS })
                        : t("support_attach_hint")
                    }
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  {/* Bouton reconnaissance vocale */}
                  <VoiceInputButton
                    voiceRecognition={voiceRecognition}
                    size="icon"
                    className="shrink-0"
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting || isSearchingKB || !message.trim()}
                    className="p-2 rounded-xl text-primary-foreground disabled:opacity-50 disabled:bg-muted bg-primary hover:bg-primary/90 transition-colors flex items-center justify-center shrink-0"
                  >
                    {isSubmitting || isSearchingKB ? (
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </form>

              <div className="hidden md:block text-center mt-3 mb-1">
                <p className="text-[10px] text-muted-foreground font-medium">
                  {t("support_keyboard_hint")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
